import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const answered = (q, v) => (q.type === 'multi' ? Array.isArray(v) && v.length > 0 : v !== undefined && v !== null && v !== '');

// One question at a time: single / multi / text / number. `compact` = homepage embed.
export const JourneyInterview = ({ questions, initialAnswers = {}, onComplete, busy, compact, intro }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const q = questions[idx];
  const last = idx === questions.length - 1;
  if (!q) return null;
  const cur = answers[q.key];
  const setAns = (v) => setAnswers((a) => ({ ...a, [q.key]: v }));
  const advance = (next) => { if (!last) setTimeout(() => setIdx((i) => i + 1), 180); else onComplete(next); };
  const pickSingle = (key) => { const next = { ...answers, [q.key]: key }; setAnswers(next); advance(next); };
  const toggleMulti = (key) => { const arr = Array.isArray(cur) ? cur : []; setAns(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]); };
  const next = () => { if (!answered(q, cur)) return; advance(answers); };

  return (
    <div data-testid="journey-interview" className={compact ? '' : 'rounded-2xl bg-white border-2 border-[#E9D5FF] shadow-sm p-6 md:p-8'}>
      {intro && !compact && (
        <div className="mb-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#7E22CE] mb-1">Step 1 · Quick interview</div>
          <h2 className="text-2xl font-black tracking-tight text-[#0F172A]">{intro.title}</h2>
          <p className="text-sm text-slate-600 mt-1">{intro.text}</p>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4" data-testid="journey-progress">
        {questions.map((qq, i) => (
          <div key={qq.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i < idx || answered(qq, answers[qq.key]) ? 'bg-[#7E22CE]' : i === idx ? 'bg-[#C4B5FD]' : 'bg-slate-200'}`} />
        ))}
        <span className="text-[11px] font-black text-slate-500 ml-1 whitespace-nowrap">{idx + 1} / {questions.length}</span>
      </div>
      <h3 className={`font-black tracking-tight text-[#0F172A] ${compact ? 'text-lg' : 'text-xl md:text-2xl'}`} data-testid="journey-question-title">{q.title}</h3>
      {q.help && <p className="text-xs text-slate-500 mt-1">{q.help}</p>}

      {(q.type === 'single' || q.type === 'multi' || !q.type) && (
        <div className={`grid gap-2 mt-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {q.options.map((o) => {
            const on = q.type === 'multi' ? (Array.isArray(cur) && cur.includes(o.key)) : cur === o.key;
            return (
              <button key={o.key} type="button" onClick={() => (q.type === 'multi' ? toggleMulti(o.key) : pickSingle(o.key))} disabled={busy}
                className={`text-left rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all flex items-center justify-between gap-2 ${on ? 'border-[#7E22CE] bg-[#F5F3FF] text-[#4C1D95]' : 'border-slate-200 bg-white hover:border-[#C4B5FD] text-slate-700'}`}
                data-testid={`journey-option-${q.key}-${o.key}`}>
                <span>{o.label}</span>
                {on && <Check size={16} className="text-[#7E22CE] shrink-0" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      )}
      {(q.type === 'text' || q.type === 'number') && (
        <Input type={q.type} value={cur ?? ''} onChange={(e) => setAns(q.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} className="mt-4 h-11" placeholder="Your answer" data-testid={`journey-input-${q.key}`} />
      )}

      <div className="flex items-center justify-between mt-5">
        <button type="button" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0 || busy} className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-500 disabled:opacity-30" data-testid="journey-back">
          <ArrowLeft size={13} /> Back
        </button>
        {busy ? <Loader2 size={18} className="animate-spin text-[#7E22CE]" /> : (
          (q.type === 'multi' || q.type === 'text' || q.type === 'number' || (!last && answered(q, cur))) && (
            <Button type="button" onClick={next} disabled={!answered(q, cur)} className="h-9 px-4 bg-[#7E22CE] hover:bg-[#6B21A8] text-white text-xs font-black uppercase tracking-widest rounded-full" data-testid="journey-next">
              {last ? 'See my recommendation' : 'Next'} <ArrowRight size={13} className="ml-1" />
            </Button>
          )
        )}
      </div>
    </div>
  );
};
